import { useCallback, useEffect, useState } from 'react';
import { del, get, patch, post } from '../lib/api';
import IconPicker from './IconPicker';
import { ErrorNote, Sheet, Spinner } from './ui';
import { money } from '../lib/format';
import type { Child, Extra } from '../lib/types';

/** Parents define what earns extra money. */
export default function ExtrasManager({
  children,
  currency,
  onClose,
}: {
  children: Child[];
  currency: string;
  onClose: () => void;
}): JSX.Element {
  const [extras, setExtras] = useState<Extra[] | null>(null);
  const [editing, setEditing] = useState<Extra | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    get<{ extras: Extra[] }>('/api/extras')
      .then((res) => setExtras(res.extras))
      .catch((err) => setError((err as Error).message));
  }, []);
  useEffect(load, [load]);

  async function retire(extra: Extra) {
    if (!confirm(`Remove "${extra.title}"? Past claims are kept.`)) return;
    await del(`/api/extras/${extra.id}`);
    load();
  }

  if (editing) {
    return (
      <ExtraForm
        extra={editing === 'new' ? null : editing}
        children={children}
        currency={currency}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />
    );
  }

  return (
    <Sheet title="Extra money" onClose={onClose}>
      <ErrorNote error={error} />
      <p className="small muted">Things the kids can do as often as they like, for money on top of pocket money.</p>
      {!extras ? (
        <Spinner />
      ) : (
        <div className="stack">
          {extras.map((extra) => {
            const who = extra.child_id ? children.find((c) => c.id === extra.child_id)?.name ?? 'one child' : 'everyone';
            return (
              <div key={extra.id} className="chore" onClick={() => setEditing(extra)} role="button" tabIndex={0}>
                <span className="chore-icon">{extra.icon}</span>
                <span className="grow">
                  <span className="chore-title">{extra.title}</span>
                  <span className="row wrap tiny muted" style={{ gap: 6 }}>
                    <span>{money(extra.amount, currency)}{extra.pricing === 'hourly' ? ' an hour' : ' each'}</span>
                    <span>· for {who}</span>
                    {extra.needs_approval === 0 && <span className="badge">auto-approve</span>}
                  </span>
                </span>
                <button
                  className="btn ghost small"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    void retire(extra);
                  }}
                >
                  Remove
                </button>
              </div>
            );
          })}
          {extras.length === 0 && <p className="muted center">Nothing yet.</p>}
        </div>
      )}
      <button className="btn block" type="button" style={{ marginTop: 14 }} onClick={() => setEditing('new')}>
        + Add an extra
      </button>
    </Sheet>
  );
}

function ExtraForm({
  extra,
  children,
  currency,
  onClose,
  onSaved,
}: {
  extra: Extra | null;
  children: Child[];
  currency: string;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [title, setTitle] = useState(extra?.title ?? '');
  const [icon, setIcon] = useState(extra?.icon ?? '⭐');
  const [notes, setNotes] = useState(extra?.notes ?? '');
  const [pricing, setPricing] = useState<'fixed' | 'hourly'>(extra?.pricing ?? 'fixed');
  const [amount, setAmount] = useState(extra?.amount ?? 20);
  const [childId, setChildId] = useState<number | null>(extra?.child_id ?? null);
  const [needsApproval, setNeedsApproval] = useState((extra?.needs_approval ?? 1) === 1);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setError(null);
    if (!title.trim()) {
      setError('Give it a name.');
      return;
    }
    setBusy(true);
    const body = { title: title.trim(), icon, notes: notes.trim(), pricing, amount, child_id: childId, needs_approval: needsApproval };
    try {
      if (extra) await patch(`/api/extras/${extra.id}`, body);
      else await post('/api/extras', body);
      onSaved();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title={extra ? 'Edit extra' : 'New extra'} onClose={onClose}>
      <ErrorNote error={error} />
      <label className="field">
        <span>Name</span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Read a German book" />
      </label>
      <IconPicker value={icon} onChange={setIcon} />
      <div className="chips" style={{ marginBottom: 14 }}>
        <button type="button" className={`chip${pricing === 'fixed' ? ' on' : ''}`} onClick={() => setPricing('fixed')}>Fixed amount each time</button>
        <button type="button" className={`chip${pricing === 'hourly' ? ' on' : ''}`} onClick={() => setPricing('hourly')}>By the hour</button>
      </div>
      <label className="field">
        <span>{pricing === 'hourly' ? `${currency} per hour (15-minute steps when claiming)` : `${currency} each time`}</span>
        <input type="number" min={0} step="0.5" value={amount} onChange={(e) => setAmount(Number(e.target.value))} />
      </label>
      <label className="field">
        <span>For</span>
        <select value={childId ?? ''} onChange={(e) => setChildId(e.target.value ? Number(e.target.value) : null)}>
          <option value="">Everyone</option>
          {children.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Notes for the kids (optional)</span>
        <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any book over 100 pages counts" />
      </label>
      <div className="toggle">
        <span className="small">Needs a parent's OK<br /><span className="tiny muted">Off means it counts as soon as they log it.</span></span>
        <input type="checkbox" checked={needsApproval} onChange={(e) => setNeedsApproval(e.target.checked)} />
      </div>
      <button className="btn block" type="button" style={{ marginTop: 14 }} onClick={save} disabled={busy}>
        {busy ? 'Saving…' : 'Save'}
      </button>
    </Sheet>
  );
}
